import React, { useState, useEffect } from 'react';
import { getMyBalanceMovements, getUserStats } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Wallet, ArrowUpRight, ArrowDownRight, TrendingUp, TrendingDown } from 'lucide-react';

export default function UserBalance() {
  const { user, refreshUser } = useAuth();
  const [movements, setMovements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await refreshUser();
        const [movRes, statsRes] = await Promise.all([
          getMyBalanceMovements(),
          getUserStats(),
        ]);
        setMovements(movRes.data);
        setStats(statsRes.data);
      } catch (err) {
        console.error('Error fetching balance data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const totalCredits = movements
    .filter((m) => m.type === 'credit')
    .reduce((sum, m) => sum + m.amount, 0);
  const totalDebits = movements
    .filter((m) => m.type === 'debit')
    .reduce((sum, m) => sum + m.amount, 0);

  return (
    <div className="space-y-6" data-testid="user-balance">
      <div>
        <h1 className="text-4xl font-black tracking-tight">Mi Saldo</h1>
        <p className="text-muted-foreground mt-1">
          Resumen de tu saldo y movimientos
        </p>
      </div>

      {/* Balance Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-accent/20 to-accent/5 border-accent/30">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-lg bg-accent/20">
                <Wallet className="w-8 h-8 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">
                  Saldo Actual
                </p>
                <p className="text-4xl font-black text-accent metric-value">
                  {formatCurrency(user?.balance || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border/40">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-lg bg-green-500/20">
                <TrendingUp className="w-8 h-8 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">
                  Total Recibido
                </p>
                <p className="text-3xl font-black text-green-400 metric-value">
                  {formatCurrency(totalCredits)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border/40">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-lg bg-red-500/20">
                <TrendingDown className="w-8 h-8 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">
                  Total Gastado
                </p>
                <p className="text-3xl font-black text-red-400 metric-value">
                  {formatCurrency(totalDebits)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Movements Table */}
      <Card className="bg-card border-border/40">
        <CardHeader>
          <CardTitle className="text-xl font-bold flex items-center gap-2">
            <Wallet className="w-5 h-5 text-primary" />
            Movimientos de Saldo
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {movements.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Wallet className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay movimientos de saldo</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead>Tipo</TableHead>
                    <TableHead>Monto</TableHead>
                    <TableHead>Razón</TableHead>
                    <TableHead>Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.map((m) => (
                    <TableRow
                      key={m.id}
                      className="border-border/30 hover:bg-secondary/30"
                    >
                      <TableCell>
                        {m.type === 'credit' ? (
                          <div className="flex items-center gap-2 text-green-400">
                            <ArrowUpRight className="w-4 h-4" />
                            <span className="font-medium">Crédito</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-red-400">
                            <ArrowDownRight className="w-4 h-4" />
                            <span className="font-medium">Débito</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell
                        className={`font-mono font-bold ${
                          m.type === 'credit' ? 'text-green-400' : 'text-red-400'
                        }`}
                      >
                        {m.type === 'credit' ? '+' : '-'}
                        {formatCurrency(m.amount)}
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-xs truncate">
                        {m.reason}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(m.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
